<template>
  <div role="tablist"
       :aria-label="tablistLabel"
       class="s-tab-header d-flex"
  >
    <SButton
      v-for="tab in tabs"
      :id="`tab-header-${tab.id}`"
      :key="tab.id"
      :data-cy="`tab-header-${tab.id}`"
      :data-test="`tab-header-${tab.id}`"
      type="button"
      role="tab"
      variant="link"
      :aria-selected="`${isActiveTab(tab)}`"
      :aria-controls="`tabpanel-${tab.id}`"
      class="s-tab-button font-weight-bold text-truncate"
      :class="{ 's-tab-button--active': isActiveTab(tab) }"
      @click="setActiveTabId(tab)"
    >
      <slot v-if="$hasSlotContent($slots['tab-icon'])"
            name="tab-icon"
            :tab="tab"
      />
      <div v-else class="text-truncate">{{ tab.text }}</div>
    </SButton>
  </div>
</template>

<script>
import SButton from 'src/components/base/SButton.vue'

export default {
  name: 'STabHeader',
  components: { SButton },
  props: {
    tablistLabel: {
      type: String,
      default: '',
    },

    tabs: {
      type: Array,
      default: () => [],
      validator: function (tabs) {
        return tabs.every((tab) => {
          return (
            typeof tab === 'object' &&
            Object.prototype.hasOwnProperty.call(tab, 'id') &&
            typeof tab.id === 'string' &&
            Object.prototype.hasOwnProperty.call(tab, 'text') &&
            typeof tab.text === 'string'
          )
        })
      },
    },

    defaultActiveTabIndex: {
      type: Number,
      default: 0,
    },

    saveActiveTabInRoute: {
      type: Boolean,
      default: true,
    },

    activeTabPostFix: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    activeTabId: null,
  }),

  computed: {
    routeQueryName() {
      if (this.activeTabPostFix) return `activeTab-${this.activeTabPostFix}`
      return 'activeTab'
    },
  },

  emits: ['change'],

  methods: {
    async setActiveTabId(tab) {
      this.activeTabId = tab.id

      if (!this.saveActiveTabInRoute) {
        this.$emit('change', tab)
        return
      }
      await this.$router.replace({ query: { ...this.$route.query, [this.routeQueryName]: tab.id } })
      this.$emit('change', tab)
    },

    isActiveTab(tab) {
      return this.activeTabId === tab.id
    },
  },

  created() {
    if (!this.tabs.length) return

    if (this.$route.query[this.routeQueryName]) return (this.activeTabId = this.$route.query[this.routeQueryName])

    this.activeTabId = this.tabs[this.defaultActiveTabIndex].id
  },
}
</script>

<style scoped lang="scss">
.s-tab-button {
  flex: 1;
  color: $color-gray-300;

  &:hover,
  &:focus-visible {
    color: $color-primary;
  }
}

.s-tab-button--active {
  color: $color-primary;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    height: 2px;
    width: 100%;
    background-color: $color-primary;
    left: 0;
    bottom: 0;
    margin: 0 auto;
    border-radius: 2px;
  }

  &:hover,
  &:focus-visible {
    color: $color-primary;
  }
}
</style>

<!--
NOTE:
Use this together with STabPanel.vue, in order to create an accessible tab component.
Dont use this component as a standalone!

`tab.id` in the `tabs` should be the same as the corrisponding `tabId` of the `STabPanel.vue` component

TODO:
move this small documentation to a markdown file, when storybook is ready
-->
